import { Injectable, OnDestroy } from "@angular/core";
import { merge, Observable, Subject } from "rxjs";
import { debounceTime, mapTo } from "rxjs/operators";
import { environment } from "src/environments/environment";

/** Payload of the `video-alert.created` SSE event. */
export interface VideoAlertCreated {
  id: number;
  tenantId: number;
  cameraId: number;
  dlModelId: number;
  groupId: string | null;
  objectId: string | null;
  label: string | null;
  value: string | null;
  snapshotImage: string | null;
  snapshotVideo: string | null;
  /** Tenant-local "YYYY-MM-DD HH:mm:ss"; display as-is. */
  videoTimestamp: string | null;
  accuracy: number | null;
  createdAt: string;
  /** Facial recognition alerts only: who was recognised. */
  isGuest?: boolean;
  employeeId?: number | null;
  employeeName?: string | null;
  employeeCategory?: EmployeeCategory | null;
  employeeImageUrl?: string | null;
  snapshotImageUrl?: string | null;
}

/** Employee category attached to a people-detection event (e.g. "VIP"). */
export interface EmployeeCategory {
  id: number;
  name: string;
  description: string | null;
}

/** Payload of the `people_detection.event_created` SSE event. */
export interface PeopleDetectionCreated {
  eventId: number;
  employeeId: number | null;
  cameraId: number;
  employeeCategory: EmployeeCategory | null;
  /** Tenant-local "YYYY-MM-DD HH:mm:ss"; display as-is. */
  videoTimestamp: string | null;
}

/** Employee category filter accepted by the stream endpoint ("other" = every non-VIP category). */
export type StreamCategory = "vip" | "other";

/** One event from a category-filtered stream (see `categoryEvents`). */
export type CategoryStreamEvent =
  | { type: "connected" }
  | { type: "video-alert"; alert: VideoAlertCreated }
  | { type: "people-detection"; detection: PeopleDetectionCreated };

interface SseFrame {
  event: string;
  id: string;
  data: string;
}

const STREAM_URL = `${environment.DOMAIN}/api/v2/video-events/stream`;
const TOKEN_KEY = `${environment.ProjectName}-token`;
const MAX_RETRY_MS = 10000;
/** Grace period so a layout re-create (route change) doesn't drop and reopen the stream. */
const RELEASE_GRACE_MS = 1000;
/** Bursts of alerts within this window trigger a single view refresh. */
const LIVE_UPDATE_DEBOUNCE_MS = 300;
/** How many recent event IDs to remember for duplicate suppression. */
const SEEN_IDS_LIMIT = 500;

/**
 * The application's single real-time connection: the shared
 * `/api/v2/video-events/stream` SSE endpoint (replaces MQTT / WebSocket alerts).
 *
 * - The authenticated layout shell calls `acquire()` / `release()`, so the stream
 *   is open only while the application is active and closes on sign-out.
 * - Native EventSource cannot send `x-auth-token`, so this reads the stream with fetch.
 * - The stream is live-only (no replay). Views must reload from REST on `connected$`
 *   and treat REST responses as the source of record.
 */
@Injectable({ providedIn: "root" })
export class VideoEventsStreamService implements OnDestroy {
  private connectedSubject = new Subject<void>();
  private videoAlertsSubject = new Subject<VideoAlertCreated>();
  private peopleDetectionsSubject = new Subject<PeopleDetectionCreated>();

  /** Emits after every (re)connect: reload REST data, events may have been missed. */
  readonly connected$: Observable<void> = this.connectedSubject.asObservable();
  /** `video-alert.created` */
  readonly videoAlerts$: Observable<VideoAlertCreated> = this.videoAlertsSubject.asObservable();
  /** `people_detection.event_created` */
  readonly peopleDetections$: Observable<PeopleDetectionCreated> = this.peopleDetectionsSubject.asObservable();

  /**
   * "Reload your data now": fires (debounced) after a (re)connect or new video alerts.
   * Views subscribe to this and refetch from REST, which stays the source of record.
   */
  readonly liveUpdate$: Observable<void> = merge(this.connected$, this.videoAlerts$.pipe(mapTo(undefined))).pipe(
    debounceTime(LIVE_UPDATE_DEBOUNCE_MS),
  );

  /**
   * A dedicated `/stream?category=…` connection, open while subscribed.
   * Only the VIP Hospitality (Penske) app uses these; everything else uses the shared stream.
   */
  categoryEvents(category: StreamCategory): Observable<CategoryStreamEvent> {
    return new Observable<CategoryStreamEvent>((subscriber) => {
      const controller = new AbortController();
      const seenAlerts = new Set<number>();
      const seenDetections = new Set<number>();
      this.readStream(`${STREAM_URL}?category=${encodeURIComponent(category)}`, controller.signal, (frame) => {
        if (frame.event === "connected") {
          subscriber.next({ type: "connected" });
          return;
        }
        const alert = this.toVideoAlert(frame);
        if (alert && this.markSeen(seenAlerts, alert.id)) {
          subscriber.next({ type: "video-alert", alert });
        }
        const detection = this.toPeopleDetection(frame);
        if (detection && this.markSeen(seenDetections, detection.eventId)) {
          subscriber.next({ type: "people-detection", detection });
        }
      });
      return () => controller.abort();
    });
  }

  private seenAlertIds = new Set<number>();
  private seenDetectionIds = new Set<number>();

  private controller: AbortController | null = null;
  private users = 0;
  private releaseTimer: any;

  acquire(): void {
    this.users++;
    clearTimeout(this.releaseTimer);
    if (!this.controller) {
      this.open();
    }
  }

  release(): void {
    this.users = Math.max(0, this.users - 1);
    if (this.users === 0) {
      clearTimeout(this.releaseTimer);
      this.releaseTimer = setTimeout(() => this.close(), RELEASE_GRACE_MS);
    }
  }

  ngOnDestroy(): void {
    clearTimeout(this.releaseTimer);
    this.close();
  }

  private close(): void {
    if (this.controller) {
      this.controller.abort();
      this.controller = null;
    }
  }

  private open(): void {
    const controller = new AbortController();
    this.controller = controller;
    this.readStream(STREAM_URL, controller.signal, (frame) => this.dispatch(frame));
  }

  /** Reads an SSE endpoint until aborted, reconnecting with backoff. */
  private readStream(url: string, signal: AbortSignal, onFrame: (frame: SseFrame) => void): void {
    (async () => {
      let failures = 0;
      while (!signal.aborted) {
        try {
          // Read the token on every attempt: connections close at JWT expiry / 15 min.
          const token = localStorage.getItem(TOKEN_KEY);
          if (!token) {
            throw new Error("Authentication token unavailable");
          }
          const response = await fetch(url, {
            method: "GET",
            headers: { Accept: "text/event-stream", "x-auth-token": token },
            cache: "no-store",
            signal,
          });
          if (!response.ok || !response.body) {
            // 401 (expired token) and 503 (consumer reconnecting) are retried below.
            throw new Error(`Video events stream returned HTTP ${response.status}`);
          }

          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          const parse = this.createParser((frame) => {
            if (frame.event === "connected") {
              failures = 0;
            }
            onFrame(frame);
          });
          while (!signal.aborted) {
            const { done, value } = await reader.read();
            if (done) {
              break;
            }
            parse(decoder.decode(value, { stream: true }));
          }
        } catch (error) {
          if (!signal.aborted && !environment.production) {
            console.warn("[video-events-stream]", url, error);
          }
        }
        if (signal.aborted) {
          break;
        }
        failures += 1;
        await new Promise((resolve) => setTimeout(resolve, Math.min(1000 * 2 ** failures, MAX_RETRY_MS)));
      }
    })();
  }

  private dispatch(frame: SseFrame): void {
    if (frame.event === "connected") {
      this.connectedSubject.next();
      return;
    }
    const alert = this.toVideoAlert(frame);
    if (alert && this.markSeen(this.seenAlertIds, alert.id)) {
      this.videoAlertsSubject.next(alert);
    }
    const detection = this.toPeopleDetection(frame);
    if (detection && this.markSeen(this.seenDetectionIds, detection.eventId)) {
      this.peopleDetectionsSubject.next(detection);
    }
  }

  private toVideoAlert(frame: SseFrame): VideoAlertCreated | null {
    const payload = frame.event === "video-alert.created" ? this.parse(frame) : null;
    return payload && Number.isInteger(payload.id) ? (payload as VideoAlertCreated) : null;
  }

  private toPeopleDetection(frame: SseFrame): PeopleDetectionCreated | null {
    const payload = frame.event === "people_detection.event_created" ? this.parse(frame) : null;
    if (!payload || !Number.isInteger(payload.event_id)) {
      return null;
    }
    return {
      eventId: payload.event_id,
      employeeId: payload.employee_id ?? null,
      cameraId: payload.camera_id,
      employeeCategory: payload.employee_category ?? null,
      videoTimestamp: payload.video_timestamp ?? null,
    };
  }

  private parse(frame: SseFrame): any {
    try {
      return JSON.parse(frame.data);
    } catch (e) {
      return null;
    }
  }

  /** Records an ID; false if it was already delivered (duplicate Kafka/SSE delivery). */
  private markSeen(seen: Set<number>, id: number): boolean {
    if (seen.has(id)) {
      return false;
    }
    seen.add(id);
    if (seen.size > SEEN_IDS_LIMIT) {
      const oldest = seen.values().next();
      if (!oldest.done) {
        seen.delete(oldest.value);
      }
    }
    return true;
  }

  /** Incremental SSE parser: handles \n, \r, \r\n line endings and `:` comment (heartbeat) lines. */
  private createParser(onFrame: (frame: SseFrame) => void): (chunk: string) => void {
    let line = "";
    let afterCR = false;
    let event = "message";
    let id = "";
    let data: string[] = [];

    const endLine = () => {
      if (line === "") {
        if (data.length) {
          onFrame({ event, id, data: data.join("\n") });
        }
        event = "message";
        id = "";
        data = [];
      } else if (!line.startsWith(":")) {
        const colon = line.indexOf(":");
        const field = colon < 0 ? line : line.slice(0, colon);
        const raw = colon < 0 ? "" : line.slice(colon + 1);
        const value = raw.startsWith(" ") ? raw.slice(1) : raw;
        if (field === "event") {
          event = value;
        } else if (field === "data") {
          data.push(value);
        } else if (field === "id" && !value.includes("\0")) {
          id = value;
        }
      }
      line = "";
    };

    return (chunk: string) => {
      for (const ch of chunk) {
        if (afterCR) {
          afterCR = false;
          if (ch === "\n") {
            continue;
          }
        }
        if (ch === "\r") {
          endLine();
          afterCR = true;
        } else if (ch === "\n") {
          endLine();
        } else {
          line += ch;
        }
      }
    };
  }
}
